import React, { useMemo, useState, useEffect } from "react";
import { Layout, Menu, Button, Space } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { isAuthed, logout } from "../../api/mockData";
import ThemeSwitcher from "../ThemeSwitcher";

const { Header } = Layout;

// 主题配置 - 与CSS中的data-theme属性对应
const THEME_CONFIG = {
  'default': { name: '江湖', color: '#F8F9FA' },
  'cyberpunk': { name: '霓虹', color: '#FF00FF' },
  'nature': { name: '自然', color: '#2E8B57' },
  'depressed': { name: '雨崩', color: '#BCAAA4' },
  'pet': { name: '萌宠', color: '#FF6B9D' },
  'lights': { name: '希望', color: '#FFD700' } // 新增万家灯火主题
};

function HeaderBar() {
  const nav = useNavigate();
  const loc = useLocation();
  const [currentTheme, setCurrentTheme] = useState('default'); // 默认主题
  const [readingProgress, setReadingProgress] = useState(0); // 阅读进度

  // 检测当前应用的data-theme属性
  useEffect(() => {
    const detectTheme = () => {
      const body = document.body;
      const theme = body.getAttribute('data-theme') || 'default';
      setCurrentTheme(theme);
    };

    // 初始检测
    detectTheme();

    // 监听主题变化
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          detectTheme();
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // 阅读进度条的实现
  useEffect(() => {
    // 滚动事件处理
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      const scrollPercent = Math.min((scrollTop / (docHeight - winHeight)) * 100, 100);
      
      setReadingProgress(scrollPercent);
    };

    // 只有在文章详情页才添加滚动事件监听
    if (loc.pathname.startsWith("/post/")) {
      window.addEventListener('scroll', handleScroll);
    }

    // 清理函数
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [loc.pathname]);

  // 根据当前主题获取标题文字
  const getTitleText = () => {
    return THEME_CONFIG[currentTheme]?.name || THEME_CONFIG['default'].name;
  };

  const selected = useMemo(() => {
    if (loc.pathname.startsWith("/archive")) return ["archive"];
    if (loc.pathname.startsWith("/about")) return ["about"];
    if (loc.pathname.startsWith("/write")) return ["write"];
    if (loc.pathname.startsWith("/projects")) return ["projects"];
    if (loc.pathname.startsWith("/auth")) return ["auth"];
    return ["home"];
  }, [loc.pathname]);

  // 恢复正常的菜单项，不包含进度条
  const items = [
    { key: "home", label: "首页" },
    { key: "archive", label: "归档" },
    { key: "write", label: "写文章" },
    { key: "projects", label: "项目" },
    { key: "about", label: "关于" },
  ];

  const onClick = ({ key }) => {
    if (key === "home") nav("/");
    if (key === "archive") nav("/archive");
    if (key === "about") nav("/about");
    if (key === "write") nav("/write");
    if (key === "projects") nav("/projects");
  };

  const authed = isAuthed();

  return (
    <Header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: "var(--theme-card-bg)", // 使用主题卡片背景色
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid var(--theme-card-border)" // 使用主题边框色
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", gap: 12 }}>
        <div
          onClick={() => nav("/")}
          className="theme-title"
          data-text={getTitleText()}  // 添加这一行
          style={{ 
            color: "var(--theme-primary)", // 使用主题主色
            fontWeight: 700, 
            cursor: "pointer", 
            letterSpacing: 1,
            fontSize: "18px",
            textShadow: "var(--theme-header-shadow)" // 使用主题阴影效果
          }}
        >
          {getTitleText()}
        </div>

        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
          <Menu
            mode="horizontal"
            selectedKeys={selected}
            items={items}
            onClick={onClick}
            style={{ 
              background: "transparent",
              border: "none",
              flex: 1
            }}
          />
          
          {/* 将进度条作为独立元素放在导航菜单旁边 */}
          {loc.pathname.startsWith("/post/") && (
            <div style={{ 
              width: '100px', 
              height: '8px', 
              backgroundColor: 'var(--theme-card-border)',
              borderRadius: '4px',
              overflow: 'hidden',
              marginLeft: '12px'
            }}>
              <div style={{
                height: '100%',
                width: `${readingProgress}%`,
                backgroundColor: 'var(--theme-primary)',
                transition: 'width 0.1s ease-out'
              }} />
            </div>
          )}
        </div>

        <Space>
          <ThemeSwitcher />
          {!authed ? (
            <Button 
              onClick={() => nav("/auth")}
              style={{
                background: "var(--theme-card-bg)",
                border: "1px solid var(--theme-card-border)",
                color: "var(--theme-text)"
              }}
            >
              登录
            </Button>
          ) : (
            <Button
              onClick={() => {
                logout();
                nav("/");
              }}
              style={{
                background: "var(--theme-card-bg)",
                border: "1px solid var(--theme-card-border)",
                color: "var(--theme-text)"
              }}
            >
              退出
            </Button>
          )}
        </Space>
      </div>
    </Header>
  );
}

export default HeaderBar;